import { IModule, ModuleContent, ModuleParams } from '../types';
import { PdfFont, PdfBrush, PdfSize, ETextAlign, PdfRect, PageGen, PdfImage } from '../../pdf/pdf';
import '../../pdf/draw/image';

const _cache = new Map<string, PdfImage>();

export function setPreloadedImages(map: Map<string, PdfImage>): void {
  _cache.clear();
  for (const [k, v] of map) {
    _cache.set(k, v);
  }
}

export const imageModule: IModule = {
  name: 'img',

  parse(source: string, attrs: Record<string, string>): ModuleContent {
    return {
      type: 'img',
      attrs,
      content: source,
    };
  },

  measure(content: ModuleContent, params: ModuleParams): PdfSize {
    const src = content.attrs.src || content.content;
    const cachedImg = src ? _cache.get(src) : undefined;

    const wAttr = content.attrs.width ? parseFloat(content.attrs.width) : NaN;
    const hAttr = content.attrs.height ? parseFloat(content.attrs.height) : NaN;

    if (!isNaN(wAttr) && !isNaN(hAttr)) {
      return new PdfSize(wAttr, hAttr);
    }

    if (!isNaN(wAttr) && cachedImg) {
      const aspect = cachedImg.height / cachedImg.width;
      return new PdfSize(wAttr, wAttr * aspect);
    }

    if (cachedImg) {
      const maxDefault = 200;
      const scale = Math.min(maxDefault / cachedImg.width, maxDefault / cachedImg.height);
      return new PdfSize(cachedImg.width * scale, cachedImg.height * scale);
    }

    return new PdfSize(!isNaN(wAttr) ? wAttr : 100, !isNaN(hAttr) ? hAttr : 100);
  },

  draw(content: ModuleContent, rect: PdfRect, align: ETextAlign, params: ModuleParams): void {
    const page = params.page!;
    if (!page) throw new Error('PageGen required for image drawing');

    const src = content.attrs.src || content.content;
    const alt = content.attrs.alt || 'Image';
    const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);

    const pdfImage = src ? _cache.get(src) : undefined;

    if (pdfImage) {
      page.drawImageFit(pdfImage, rect);
    } else {
      page.drawString(`[Image: ${alt}]`, font, PdfBrush.LightGray, null, rect, ETextAlign.Center);
    }
  },
};
